import type { Address } from "~/types";

import { formatAddress } from "./formatters/address";
import { toWholeCurrency } from "./currency";

interface UserNameProps {
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
}

export const formatUsersName = ({
  firstName = "",
  lastName = "",
}: UserNameProps = {}): string => {
  return `${firstName ? firstName : ""} ${lastName ? lastName : ""}`.trim();
};

// AddressProps is now imported from formatters/address module

interface AddressLimitedProps {
  address?: Address | null | undefined;
  show?:
    | {
        line1?: boolean;
        line2?: boolean;
        city?: boolean;
        state?: boolean;
        postalCode?: boolean;
      }
    | null
    | undefined;
}

// Re-exported from separate module to enable code splitting
export { formatAddress } from "~/util/formatters/address";

export const formatLimitedAddress = ({
  address = { line1: "", line2: "", city: "", state: null, postalCode: "" },
  show = {
    line1: false,
    line2: false,
    city: false,
    state: false,
    postalCode: false,
  },
}: AddressLimitedProps): string[] => {
  const { line1, line2, city, state, postalCode } = address || {};

  // If all address fields are empty, return an empty array
  if (!line1 && !line2 && !city && !state && !postalCode) {
    return [];
  }

  // If all show flags are false, return an empty array
  if (
    !show?.line1 &&
    !show?.line2 &&
    !show?.city &&
    !show?.state &&
    !show?.postalCode
  ) {
    return [];
  }

  // Create an object to hold the formatted address lines
  const addressToShow: Record<string, string> = {};

  // Add address lines based on the show flags
  if (show.line1 && line1) {
    addressToShow["line1"] = line1;
  }
  if (show.line2 && line2) {
    addressToShow["line2"] = line2;
  }
  if (show.city && city) {
    addressToShow["city"] = city;
  }
  if (show.state && state) {
    addressToShow["state"] = state;
  }
  if (show.postalCode && postalCode) {
    addressToShow["postalCode"] = postalCode;
  }

  return formatAddress(addressToShow);
};

export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("EN-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

export const formatCurrencyNoTrailing = (amount: number): string => {
  const formatted = formatCurrency(amount);

  // Remove trailing ".00"
  return formatted.endsWith(".00") ? formatted.slice(0, -3) : formatted;
};

export const stringToSlug = (str: string): string =>
  str.replace(/\s+/g, "-").toLowerCase();

export const slugToString = (slug: string): string => slug.replace(/-/g, " ");

export const slugToCapitalizedString = (slug: string): string => {
  const str = slugToString(slug);
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Re-exported from separate module to enable code splitting
export { formatPhoneNumber } from "~/util/formatters/phone";

export const toLowercase = (str: string): string => {
  if (!str) return "";

  return str.toLowerCase();
};

export const formatStarLastFour = (str: string): string => {
  if (!str) return "";

  return `****${str.slice(-4)}`;
};

export const numberOver99Limit = (num: number): string => {
  if (num > 99) {
    return "99+";
  }

  return num.toString();
};

interface FormatAccountingFigureProps {
  figure: number | null | undefined;
}

export const formatAccountingFigure = (
  figure: FormatAccountingFigureProps["figure"]
): string => {
  if (!figure) {
    return "-";
  }

  if (figure === 0) {
    return "-";
  }

  const wholeFigure = toWholeCurrency(figure);

  return wholeFigure < 0
    ? `($${Math.abs(wholeFigure).toFixed(2)})`
    : `$${wholeFigure.toFixed(2)}`;
};
